import { z } from 'zod';
import { ContractorSubtypeSchema, UserRoleSchema, UserStatusSchema, } from '../lib/enums';
import { MarkupSchema, TaxSchema, zDateOnly, zEmail, zHexColor, zIsoDateTime, zJurisdiction, zMoneyNonNeg, zUuid, } from './common';
// -------------------------------------------------------------------- company
export const BrandingSchema = z
    .object({
    logoFileId: zUuid.nullable().optional(),
    primaryColor: zHexColor.nullable().optional(),
    accentColor: zHexColor.nullable().optional(),
    displayName: z.string().max(200).nullable().optional(),
    tagline: z.string().max(300).nullable().optional(),
    /** Free-text footer line (e.g. license summary) rendered on proposals. */
    footerText: z.string().max(500).nullable().optional(),
})
    .strict();
export const CompanySettingsSchema = z
    .object({
    defaultMarkup: MarkupSchema.nullable().optional(),
    /**
     * Applies to `kind: 'material'` lines only, in place of `defaultMarkup`.
     * Null/absent leaves material lines on the general markup — the behaviour
     * of every estimate written before this field existed.
     */
    defaultMaterialsMarkup: MarkupSchema.nullable().optional(),
    defaultTax: TaxSchema.nullable().optional(),
    /** Default terms text appended to new estimates' proposals. */
    defaultTerms: z.string().max(20000).nullable().optional(),
    /** IANA zone for time-clock and finance day boundaries when the device sends none; invalid → UTC. */
    timeZone: z.string().max(64).nullable().optional(),
    /** Labor role used for members without their own (RolesUsersPlan §4.5); must be this company's, else unrated. */
    defaultLaborRoleId: zUuid.nullable().optional(),
})
    .strict();
export const CompanySchema = z.object({
    id: zUuid,
    name: z.string().min(1).max(200),
    isContractor: z.boolean(),
    isSubcontractor: z.boolean(),
    contractorSubtype: ContractorSubtypeSchema.nullable(),
    branding: BrandingSchema,
    defaultJurisdiction: zJurisdiction.nullable(),
    settings: CompanySettingsSchema,
    createdAt: zIsoDateTime,
});
export const UpdateCompanyRequestSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    defaultJurisdiction: zJurisdiction.nullable().optional(),
    branding: BrandingSchema.optional(),
    settings: CompanySettingsSchema.optional(),
})
    .strict();
export const UpdateCapabilitiesRequestSchema = z
    .object({
    isContractor: z.boolean(),
    isSubcontractor: z.boolean(),
    contractorSubtype: ContractorSubtypeSchema.nullable(),
})
    .strict()
    .superRefine((v, ctx) => {
    if (!v.isContractor && !v.isSubcontractor) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'company must be a contractor, a subcontractor, or both',
        });
    }
    if (!v.isContractor && v.contractorSubtype !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'contractorSubtype must be null when isContractor is false',
            path: ['contractorSubtype'],
        });
    }
});
// --------------------------------------------------------------------- trades
// isPrimary mirrors company_trades.rank = 0 (0018). Defaults to false so
// payloads from servers/clients that predate the field still parse; the schema
// is non-strict, so older builds parsing newer responses ignore the extra key.
export const CompanyTradeSchema = z.object({
    tradeProfileKey: z.string().min(1).max(64),
    isPrimary: z.boolean().default(false),
});
// Request stays key-only: primary is assigned by the server (first trade added
// becomes primary) or changed via PATCH /company/trades/:key/primary.
export const AddCompanyTradeRequestSchema = CompanyTradeSchema.pick({ tradeProfileKey: true });
export const CompanyTradesResponseSchema = z.array(CompanyTradeSchema);
// PATCH /company/trades/:key/primary — no body; returns the full trade list.
export const SetPrimaryTradeResponseSchema = CompanyTradesResponseSchema;
// ------------------------------------------------------------------- licenses
/**
 * License records are stored AS ENTERED and NEVER verified by the product.
 * Surfaces that display them must label them as contractor-entered data.
 */
export const LicenseRecordSchema = z.object({
    id: zUuid,
    jurisdiction: zJurisdiction,
    trade: z.string().max(120).nullable(),
    licenseNumber: z.string().min(1).max(120),
    classification: z.string().max(120).nullable(),
    authority: z.string().max(200).nullable(),
    expiresAt: zDateOnly.nullable(),
    createdAt: zIsoDateTime,
});
export const CreateLicenseRecordRequestSchema = z
    .object({
    jurisdiction: zJurisdiction,
    trade: z.string().max(120).nullable().optional(),
    licenseNumber: z.string().min(1).max(120),
    classification: z.string().max(120).nullable().optional(),
    authority: z.string().max(200).nullable().optional(),
    expiresAt: zDateOnly.nullable().optional(),
})
    .strict();
export const UpdateLicenseRecordRequestSchema = CreateLicenseRecordRequestSchema.partial();
// ------------------------------------------------------------------ insurance
export const InsuranceRecordSchema = z.object({
    id: zUuid,
    /** Free text, e.g. "general_liability", "workers_comp" — not enumerated. */
    type: z.string().min(1).max(120),
    carrier: z.string().min(1).max(200),
    policyNumber: z.string().min(1).max(120),
    coverageAmount: zMoneyNonNeg.nullable(),
    expiresAt: zDateOnly.nullable(),
    coiFileId: zUuid.nullable(),
    createdAt: zIsoDateTime,
});
export const CreateInsuranceRecordRequestSchema = z
    .object({
    type: z.string().min(1).max(120),
    carrier: z.string().min(1).max(200),
    policyNumber: z.string().min(1).max(120),
    coverageAmount: zMoneyNonNeg.nullable().optional(),
    expiresAt: zDateOnly.nullable().optional(),
    coiFileId: zUuid.nullable().optional(),
})
    .strict();
export const UpdateInsuranceRecordRequestSchema = CreateInsuranceRecordRequestSchema.partial();
// ---------------------------------------------------------------------- users
export const CompanyUserSchema = z.object({
    id: zUuid,
    companyId: zUuid,
    email: zEmail,
    name: z.string().max(200).nullable(),
    role: UserRoleSchema,
    status: UserStatusSchema,
    totpEnabled: z.boolean(),
    createdAt: zIsoDateTime,
});
export const CreateCompanyUserRequestSchema = z
    .object({
    email: zEmail,
    name: z.string().max(200).optional(),
    role: UserRoleSchema,
    /** Local dev build: initial password set directly (no email delivery). */
    password: z.string().min(10).max(200),
})
    .strict();
export const UpdateCompanyUserRequestSchema = z
    .object({
    name: z.string().max(200).optional(),
    role: UserRoleSchema.optional(),
    status: UserStatusSchema.optional(),
})
    .strict();
//# sourceMappingURL=company.js.map